/**
 * 画布工具类
 * 提供画布绘制相关的通用辅助功能
 */
export default class CanvasUtils {
  // 新增角度范围判断方法
  static isAngleBetween (angle, startAngle, endAngle) {
  // 标准化角度到0-360范围
    angle = (angle + 360) % 360
    startAngle = (startAngle + 360) % 360
    endAngle = (endAngle + 360) % 360

    // 处理跨0度的情况
    if (startAngle > endAngle) {
      return angle >= startAngle || angle <= endAngle
    }
    return angle >= startAngle && angle <= endAngle
  }

  /**
   * 角度规整化：确保角度在0°~360°范围内
   * @static
   * @param {number} angle - 输入角度
   * @returns {number} 规整化后的角度
   */
  static normalizeAngle (angle) {
    while (angle < 0) angle += 360
    return angle % 360
  }

  /**
   * 判断角度是否在0°~180°之间（用于文字翻转判断）
   * @static
   * @param {number} angle - 输入角度
   * @returns {boolean} 是否在第一或第二象限
   */
  static isAngleInFirstOrSecondQuadrant (angle) {
    const normalized = this.normalizeAngle(angle)
    return normalized >= 0 && normalized <= 180
  }

  /**
   * 角度转弧度(以9点钟方向为0°起点)
   * @static
   * @param {number} deg - 角度值
   * @param {number} [zeroAngle=180] - 零度基准角度
   * @returns {number} 弧度值
   */
  static degToRad (deg, zeroAngle = 180) {
    const adjustedDeg = (360 - deg) - zeroAngle
    return adjustedDeg * Math.PI / 180
  }

  /**
   * 计算圆周上的点坐标
   * @static
   * @param {number} angle - 角度值
   * @param {number} distance - 距离中心点的距离
   * @param {Object} center - 中心点坐标 {x, y}
   * @param {number} [zeroAngle=180] - 零度基准角度
   * @returns {Object} 坐标点 {x, y}
   */
  static getPoint (angle, distance, center, zeroAngle = 180) {
    const rad = this.degToRad(angle, zeroAngle)
    return {
      x: center.x + distance * Math.cos(rad),
      y: center.y + distance * Math.sin(rad)
    }
  }

  /**
   * 绘制箭头头部
   * @static
   * @param {CanvasRenderingContext2D} ctx - 画布上下文
   * @param {Object} fromPoint - 起点坐标 {x, y}
   * @param {Object} toPoint - 终点坐标 {x, y}
   * @param {number} [size=15] - 箭头大小
   * @param {string} [fillStyle='#e74c3c'] - 填充色
   * @param {string} [strokeStyle='#e74c3c'] - 边框色
   */
  static drawArrowHead (ctx, fromPoint, toPoint, size = 15, fillStyle = '#e74c3c', strokeStyle = '#e74c3c') {
    const dx = toPoint.x - fromPoint.x
    const dy = toPoint.y - fromPoint.y
    const angle = Math.atan2(dy, dx)

    ctx.beginPath()
    ctx.moveTo(toPoint.x, toPoint.y)
    ctx.lineTo(
      toPoint.x - size * Math.cos(angle - Math.PI / 6),
      toPoint.y - size * Math.sin(angle - Math.PI / 6)
    )
    ctx.lineTo(
      toPoint.x - size * Math.cos(angle + Math.PI / 6),
      toPoint.y - size * Math.sin(angle + Math.PI / 6)
    )
    ctx.closePath()

    ctx.fillStyle = fillStyle
    ctx.fill()
    ctx.strokeStyle = strokeStyle
    ctx.stroke()
  }

  /**
   * 将角度值转换为度分秒格式
   * @static
   * @param {number} angle - 角度值（浮点数）
   * @returns {Object} 包含度、分、秒的对象 { deg, min, sec }
   */
  static convertAngleToDMS (angle) {
    // 取整度部分
    const deg = Math.floor(angle)
    // 计算分部分
    const minutes = (angle - deg) * 60
    const min = Math.floor(minutes)
    // 计算秒部分（四舍五入到整数）
    const sec = Math.round((minutes - min) * 60)

    return { deg, min, sec }
  }

  /**
   * 将角度值格式化为度分秒字符串
   * @static
   * @param {number} angle - 角度值（浮点数）
   * @returns {string} 格式化后的度分秒字符串（如 "45°30′15″"）
   */
  static formatAngleToDMS (angle) {
    const { deg, min, sec } = this.convertAngleToDMS(angle)
    return `${deg}°${min}′${sec}″`
  }
}
